/**
 * This service provides an interface for loading and saving communication preferences (aka "e-delivery") for
 * a participant. It also wraps our API calls with promises for convenience.
 *
 * It currently only handles the registration and enrollment flows where we are only dealing with one plan
 * for the participant. See login-ui/client/src/app/eConsent/EConsentModule.js for the communication preferences
 * that handles multiple plans for the Profile page.
 */
import ExternalLogger from "../logger/ExternalLogger";
import eDeliveryEvents from "./event/EDeliveryEvents";

var EDeliveryService = function ($q, eventBus, EDeliveryFactory) {
    const logger = ExternalLogger.getInstance("EDeliveryService");

    /**
     * Get the participant's communication preference for e-delivery.
     *
     * This is a convenience method to promisify the response from EDeliveryFactory.retrieveParticipantConsent().
     
     * @param  {String}  gaId The participant's group ID
     * @return {Promise}      A promise to resolve the participant's communication preference for e-delivery
     */
    function getParticipantConsent(gaId) {
        var deferred = $q.defer();

        EDeliveryFactory.retrieveParticipantConsent.query({ gaId: gaId }).$promise.then(
            function retrieveParticipantEDeliveryConsentSuccess(response) {
                if (!response || !response.data) {
                    logger.error(
                        "No response for participant's communication preferences for group id {0}.",
                        [gaId]
                    );
                    deferred.reject();
                }

                deferred.resolve(response.data);
            },
            function retrieveParticipantEDeliveryConsentFailure(err) {
                logger.error(
                    "Failed to retrieve participant's communication preferences for group id {0}. Error: {1}",
                    [gaId, JSON.stringify(err)]
                );
                deferred.reject();
            }
        );

        return deferred.promise;
    }

    /**
     * Store the participant's communication preference information in the factory so we can share it between
     * the inline e-delivery directive (EDeliveryInlineDirective) and the parent view.
     *
     * @param  {String} gaId The participant's group ID
     * @param  {Object} data The response data from the participant's eConsent query
     */
    function storeParticipantConsent(gaId, data) {
        EDeliveryFactory.setGaId(gaId);
        EDeliveryFactory.setAllowedToOverwrite(data.allowedToOverwrite);
        EDeliveryFactory.setPlanName(data.planName);
        EDeliveryFactory.setPptConsent(data.pptConsent);
        EDeliveryFactory.setEDeliveryOption(data.eDeliveryOption);
        EDeliveryFactory.setDocTypes(data.docTypes);
        EDeliveryFactory.setEConsentemailAddress(data.econsentemailAddress);
        EDeliveryFactory.setSecondaryEmail(data.secondaryEmailAddress);
    }

    /**
     * Initialize the e-delivery service by fetching the eConsent data for the participant and storing it
     * in the factory. Return whether or not the participant is allowed to change their communication preference.
     *
     * This is used so the parent view can determine whether or not the communication preferences component
     * should display.
     *
     * @param  {String}  gaId The participant's group ID
     * @return {Promise}      A promise that the participant's communication preferences will be initialized
     */
    function initialize(gaId) {
        return getParticipantConsent(gaId).then(function (data) {
            storeParticipantConsent(gaId, data);

            return EDeliveryFactory.getAllowedToOverwrite();
        });
    }

    /**
     * Update the participant's communication preference.
     *
     * @param  {String}  flowName The name of the current flow
     * @return {Promise}          A promise that the participant's communication preference will be updated in the db
     */
    function updateParticipantEConsent(flowName) {
        var params = {
            email: EDeliveryFactory.getSelectedEmail() || "",
            consentInd: EDeliveryFactory.getSelectedConsentInd(),
            gaId: EDeliveryFactory.getGaId(),
            flowName: flowName
        };

        eventBus.dispatch(eDeliveryEvents.E_CONSENT_SUBMIT, this, {
            values: params
        });

        return EDeliveryFactory.updateParticipantConsent.query(params).$promise.then(
            function updateParticipantConsentSuccess() {
                logger.debug("Successfully updated participant's communication preferences");
                return true;
            },
            function updateParticipantConsentFailure(err) {
                logger.error(
                    "Failed to update participant's communication preferences. Parameters: {0}. Error: {1}",
                    [JSON.stringify(params), JSON.stringify(err)]
                );

                eventBus.dispatch(eDeliveryEvents.E_CONSENT_SUBMIT_INVALID, this);

                return false;
            }
        );
    }

    /**
     * Validate the e-delivery form.
     *
     * @return {Boolean} True if the form is valid; false otherwise
     */
    function validateForm() {
        var form = EDeliveryFactory.getForm();

        form["communicationPreference"].$dirty = true;

        // The email choice may not be displayed if there is only one email address
        if (form.hasOwnProperty("emailChoice")) {
            form["emailChoice"].$dirty = true;
        }

        return form.$valid;
    }

    return {
        getParticipantConsent: getParticipantConsent,
        storeParticipantConsent: storeParticipantConsent,
        initialize: initialize,
        updateParticipantEConsent: updateParticipantEConsent,
        validateForm: validateForm
    };
};

EDeliveryService.$inject = ["$q", "eventBus", "EDeliveryFactory"];
export default EDeliveryService;
